import {JSX,children,Show,createSignal} from 'solid-js';
import { css } from '@emotion/css';
import { sharedTheme as theme } from 'Shared/ThemeProvider';

/*
	A standard wrapper for inputs.
	For problem input types consider creating special wrappers, e.g. <FloatWrap>, <UploaderWrap>, etc.
	For custom jobs perhaps don't use it at all.

	Wrappers are often layed out using flex. In this case its probably simpler to use gap than margins.
*/


const fieldWrapStyle = (narrow:boolean=false) => css({   //XXX would =true better?
	width: narrow ? 'unset' : '100%',
});

export const labelStyle = () => css({
	margin: '6px 0',
	fontWeight: 'bold',
	fontSize: 12,
	color: theme().palette.label,
	fontFamily: 'inherit',
	cursor: 'pointer',
	whiteSpace: 'nowrap',

	padding: 0  //TODO remove/look at again after removing Less
});

const disableStyle = () => css({
	color: 'grey'
});

export const alertStyle = () => css({
	color: 'red'
});

const errorStyle = () => css({
	color: 'red'
});

const instructionsAfterLabelStyle = () => css({
	lineHeight: '1.2em',
	margin: '2px 0px',
	fontSize: '0.8em',
	color: '#4d4d4d',  //TODO dont think I should have to have this
});	


export const noteStyle = () => css({
	fontSize: '0.8em',
	lineHeight: '1.2em',
	margin: '2px 0px',
	color: theme().palette.label  //XXX do we need this?
});

export const labelOnLeftStyle = () => css({
	display: 'flex',
	gap: '0.5em'
});

const labelOnRightStyle = () => css({
	display: 'flex',
	gap: '0.3em'
});

interface IWrap {
	label:string|JSX.Element;
//	children: InputType;
	children: JSX.Element;
	required?:boolean;
	id?: string;
	disable?:boolean;
	alert?:boolean;
	instructions?:string;
	instructionsAfterLabel?:string;
	notes?:string;
	error?:string;
	class?:string
	css?:object;
	narrow?:boolean
	labelClass?:string
	/* Styles applied to the inner row holding the label and value (LabelOnLeftWrap) */
	rowCss?:object;
	/* Is the label to the left of the value? Otherwise its above */
}

export function Wrap(P:IWrap)
{
	const c = children(() => P.children as HTMLElement);
	const childId = c()?.id?.replace(/^(wrap-)/,''); 

	const [error,setError] = createSignal(undefined);

	const checkError = (e:Event) =>  setError(e.target!.dataset?.error);  //TODO remove this dataset stuff

	return (
		<div classList={{
			[fieldWrapStyle(P.narrow)]:true, 
			[disableStyle()]:P.disable,
			[css(P.css)]: P.css,
		}}
			onInput={(e:Event) => checkError(e)}
			onChange={(e:Event) => checkError(e)}
		>
			<Show when={P.instructions}>
				<div class={noteStyle()}>{P.instructions}</div> 
			</Show>

			<label for={childId} classList={{
				[labelStyle()]:true, 
				[disableStyle()]:P.disable,
				[alertStyle()]:P.alert,
				[P.labelClass ?? 'unused']: P.labelClass
			}}>
				{P.label}
				<Show when={P.required}>&#42;</Show>
			</label>

			<Show when={P.instructionsAfterLabel}>
				<div class={instructionsAfterLabelStyle()}>{P.instructionsAfterLabel}</div>
			</Show>

			<Show when={error()}>
				<div class={errorStyle()}>
					{ capitalize(error()!)}
				</div>
			</Show>

			{c()}

			<Show when={P.notes}>
				<div class={noteStyle()}>{P.notes}</div>
			</Show>
		</div>
	);
}	

export function LabelOnLeftWrap(P:IWrap)
{
	const c = children(() => P.children);
	const childId = c()?.id?.replace(/^(wrap-)/,''); 

	return (
		<div classList={{
			[fieldWrapStyle(P.narrow)]:true, 
			[disableStyle()]:P.disable,
			[P.class]: P.class,
			[css(P.css)]: P.css
		}}>
			<Show when={P.instructions}>
				<div class={noteStyle()}>{P.instructions}</div> 
			</Show>

			<div classList={{
				[labelOnLeftStyle()]:true,
				[css(P.rowCss)]: P.rowCss
			}}>
				<label for={childId} classList={{
					[labelStyle()]:true,
					[disableStyle()]:P.disable,
					[alertStyle()]:P.alert,
					[P.labelClass ?? 'unused']: P.labelClass
				}}>
					{P.label}
					<Show when={P.required}>&#42;</Show>
				</label>

				{c()}
			</div>

			<Show when={P.notes}>
				<div class={noteStyle()}>{P.notes}</div>
			</Show>

			<Show when={P.error}>
				<div class={errorStyle()}>
					{ capitalize(P.error)}
				</div>
			</Show>
		</div>
	);
}

export function LabelOnRightWrap(P:IWrap)
{
	const c = children(() => P.children);
	const childId = c()?.id?.replace(/^(wrap-)/,''); 

	return (
		<div classList={{
			[fieldWrapStyle(P.narrow)]:true, 
			[disableStyle()]:P.disable,
			[P.class]: P.class,
			[css(P.css)]: P.css
		}}>
			<Show when={P.instructions}>
				<div class={noteStyle()}>{P.instructions}</div> 
			</Show>

			<div class={labelOnRightStyle()}> 
				{c()}

				<label for={childId} classList={{
					[labelStyle()]:true, 
					[disableStyle()]:P.disable,
					[alertStyle()]:P.alert,
					[P.labelClass ?? 'unused']: P.labelClass
				}}>
					{P.label}
					<Show when={P.required}>&#42;</Show>
				</label>
			</div>

			<Show when={P.notes}>
				<div class={noteStyle()}>{P.notes}</div>
			</Show>

			<Show when={P.error}>
				<div class={errorStyle()}>
					{ capitalize(P.error)}
				</div>
			</Show>
		</div>
	);
}

/* --- Private: --- */

function capitalize(s:string)
{
	return s.charAt(0).toUpperCase() + s.slice(1);
}

